/**
 * DOM marker discovery and live part binding.
 *
 * A cloned template fragment is walked once to associate compiler markers with
 * comments or attributes. Attribute-like parts update only their owning DOM
 * property, attribute, boolean presence, or event listener, memoizing the
 * last committed value so identical re-renders never touch the DOM (G5).
 * Composite attributes (`attr="a ${x} b ${y}"`) recover their static segments
 * from the attribute's authored value and commit one joined string (G4).
 * Element parts (G1) bind directly to an element via a valueless marker
 * attribute rather than a comment or a named attribute: presence-token
 * strings, `ref()`, and `spread()` bags all resolve here. Node-like parts
 * receive explicit start/end marker ranges and delegate reconciliation to the
 * node module. This keeps marker lookup separate from value materialization
 * while preserving surgical updates and deterministic cleanup.
 *
 * Marker POSITIONS are a property of the compiled template, not of any clone,
 * so the mount path never searches this module's maps: it walks a template once
 * and replays a positional plan (`render-dom-plan.ts`) against every later
 * clone. What remains here is the part CONSTRUCTORS both paths share, plus the
 * name-keyed binder hydration needs, because server DOM is not a clone and has
 * no recorded shape to replay.
 */
import { commitEventListener, createAttributePart, REMOVED } from './render-dom-attribute-parts.js';
import { assertValidAttributeName, endMarker, isAttributePartType, isCache, isConditional, isElementPartType, isLive, isNode, isRef, isRepeat, isSpread, isTemplateInstanceValue, isUntil, resolveValue, textValue, runtimeError, } from './render-values.js';
import { normalizeAttributeName } from './render-template.js';
import { updateNodePart } from './render-dom-nodes.js';
import { destroyMaterialized, destroyRepeatEntry, removeRange, unmountInstanceIfOwned, } from './render-dom-range.js';
import { isRawHtml } from '../ui/html-escape.js';
/**
 * Binds each part spec to the node its marker resolved to, by NAME.
 *
 * This is the hydration path only. `maps.ends` is therefore always present:
 * the end comment already sits in the server-rendered tree and every node part
 * is flagged so its first commit adopts that range instead of rebuilding it.
 * The mount path resolves by position instead (`bindPlan`), because a clone's
 * shape is a property of the compiled template and never has to be searched
 * for twice.
 */
export function buildDomParts(template, maps, documentObj, domAdapter) {
    const hydrating = maps.ends !== undefined;
    return template.partSpecs.map((spec) => {
        if (isElementPartType(spec.type)) {
            return createElementPart(spec, maps.elementMarkers.get(spec.marker));
        }
        if (isAttributePartType(spec.type)) {
            const info = maps.attributes.get(spec.marker);
            return createAttributePart(spec, info?.element, info?.name ?? '', info?.value ?? '');
        }
        const owned = maps.rangeParents?.get(spec.marker);
        if (owned) {
            const ranged = createNodePart(spec, undefined, undefined, owned, documentObj, domAdapter);
            if (hydrating)
                ranged.hydrating = true;
            return ranged;
        }
        const bounds = maps.siblingBounds?.get(spec.marker);
        if (bounds) {
            const bounded = createNodePart(spec, bounds.anchor, bounds.end, undefined, documentObj, domAdapter);
            if (hydrating)
                bounded.hydrating = true;
            return bounded;
        }
        const anchor = maps.comments.get(spec.marker);
        if (!anchor?.parentNode) {
            throw runtimeError('RT401', `Missing comment marker "${spec.marker}" for node part ${spec.index} — the template's static HTML may be malformed.`);
        }
        const existingEnd = maps.ends?.get(endMarker(spec.index));
        let end = existingEnd;
        if (!end) {
            end = documentObj.createComment(endMarker(spec.index));
            anchor.parentNode.insertBefore(end, anchor.nextSibling);
        }
        const part = createNodePart(spec, anchor, end, undefined, documentObj, domAdapter);
        if (hydrating)
            part.hydrating = true;
        return part;
    });
}
// ---------------------------------------------------------------------------
// Element parts (G1): bind directly to the element via a valueless marker
// attribute. A resolved value is either "clear everything", a presence-token
// string, a DOM reference sink (`ref()`/bare callback), or an attribute bag
// (`spread()`/plain object) routed like attribute-part prefixes.
// ---------------------------------------------------------------------------
/**
 * A plain, unbranded, non-Node object is an implicit attribute bag for
 * element parts — mirrors render-string.ts's `isPlainAttributeBag` so DOM
 * and string rendering agree on what counts as a bag. Without this guard, a
 * template instance, DOM node, or any other branded descriptor (`repeat()`,
 * `conditional()`, `until()`, `cache()`, `rawHtml()`) accidentally bound to
 * an element part would fall through to `commitSpreadBag`, which spreads
 * its own enumerable fields onto the element as attributes — for a
 * `TemplateInstanceImpl` this leaks internal engine state (`disposers`,
 * `domAdapter`, ...) straight onto the live DOM.
 */
function isPlainAttributeBag(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return false;
    return (!isNode(value) &&
        !isTemplateInstanceValue(value) &&
        !isRawHtml(value) &&
        !isRepeat(value) &&
        !isConditional(value) &&
        !isSpread(value) &&
        !isRef(value) &&
        !isUntil(value) &&
        !isLive(value) &&
        !isCache(value));
}
function classifySpreadKey(key) {
    if (key.startsWith('@'))
        return 'event';
    if (key.startsWith('.'))
        return 'property';
    if (key.startsWith('?'))
        return 'boolean';
    return 'attribute';
}
function removeSpreadEntry(element, memo) {
    switch (memo.kind) {
        case 'event': // ubs:ignore -- this case returns, so no break is required
            if (memo.listener) {
                element.removeEventListener(memo.name, memo.listener, memo.options);
            }
            return;
        case 'property': // ubs:ignore -- this case returns, so no break is required
            element[memo.name] = undefined;
            return;
        case 'boolean': // ubs:ignore -- this case returns, so no break is required
        case 'attribute':
            element.removeAttribute(memo.name);
            return;
        default:
            return;
    }
}
function applySpreadEntry(element, kind, name, resolvedValue, memo) {
    switch (kind) {
        case 'event': // ubs:ignore -- this case returns, so no break is required
            commitEventListener(element, name, resolvedValue, memo);
            return;
        case 'property': {
            // ubs:ignore -- this case returns, so no break is required
            const next = resolvedValue ?? '';
            if (memo.hasValue && Object.is(memo.value, next))
                return;
            element[name] = next;
            memo.value = next;
            memo.hasValue = true;
            return;
        }
        case 'boolean': {
            // ubs:ignore -- this case returns, so no break is required
            const next = !!resolvedValue;
            if (memo.hasValue && memo.value === next)
                return;
            if (next)
                element.setAttribute(name, '');
            else
                element.removeAttribute(name);
            memo.value = next;
            memo.hasValue = true;
            return;
        }
        case 'attribute': {
            if (resolvedValue === null || resolvedValue === undefined || resolvedValue === false) {
                if (memo.hasValue && memo.value === REMOVED)
                    return;
                element.removeAttribute(name);
                memo.value = REMOVED;
                memo.hasValue = true;
                return;
            }
            const next = resolvedValue === true ? '' : String(resolvedValue);
            if (memo.hasValue && memo.value === next)
                return;
            element.setAttribute(name, next);
            memo.value = next;
            memo.hasValue = true;
            return;
        }
        default:
            return;
    }
}
function clearSpreadBag(element, memos) {
    for (const memo of memos.values())
        removeSpreadEntry(element, memo);
    memos.clear();
}
function commitSpreadBag(element, props, memos) {
    const nextKeys = new Set(Object.keys(props));
    for (const [rawKey, memo] of memos) {
        if (nextKeys.has(rawKey))
            continue;
        removeSpreadEntry(element, memo);
        memos.delete(rawKey);
    }
    for (const rawKey of nextKeys) {
        const kind = classifySpreadKey(rawKey);
        // Keys that become real attributes get the same RT406 validation as the
        // string renderer, so both paths reject identical inputs instead of the
        // browser throwing InvalidCharacterError from inside setAttribute.
        const name = kind === 'attribute' || kind === 'boolean'
            ? assertValidAttributeName(normalizeAttributeName(rawKey))
            : normalizeAttributeName(rawKey);
        const resolvedValue = resolveValue(props[rawKey]);
        let memo = memos.get(rawKey);
        if (!memo) {
            memo = { kind, name, hasValue: false, value: undefined, listener: null, options: undefined };
            memos.set(rawKey, memo);
        }
        applySpreadEntry(element, kind, name, resolvedValue, memo);
    }
}
function applyRef(target, element) {
    if (typeof target === 'function')
        target(element);
    else
        target.current = element;
}
export function createElementPart(spec, element) {
    let tokens = new Set();
    let refTarget = null;
    const spreadMemos = new Map();
    function clearTokens() {
        if (element)
            for (const token of tokens)
                element.removeAttribute(token);
        tokens = new Set();
    }
    function clearRef() {
        if (refTarget)
            applyRef(refTarget, null);
        refTarget = null;
    }
    function clearSpread() {
        if (element)
            clearSpreadBag(element, spreadMemos);
        else
            spreadMemos.clear();
    }
    return {
        spec,
        update(value) {
            if (!element)
                return;
            const resolvedRaw = resolveValue(value);
            // Element parts unwrap `live()` transparently (G9) — the bypass-memo
            // comparison against current DOM state is specific to property/attribute.
            const resolved = isLive(resolvedRaw) ? resolveValue(resolvedRaw.value) : resolvedRaw;
            if (resolved === null ||
                resolved === undefined ||
                resolved === true ||
                resolved === false ||
                resolved === '') {
                clearTokens();
                clearRef();
                clearSpread();
                return;
            }
            if (typeof resolved === 'string') {
                clearRef();
                clearSpread();
                // Same RT406 validation as the string renderer's presence tokens.
                const nextTokens = new Set(resolved
                    .split(/\s+/)
                    .filter((token) => token.length > 0)
                    .map(assertValidAttributeName));
                for (const stale of tokens) {
                    if (!nextTokens.has(stale))
                        element.removeAttribute(stale);
                }
                for (const token of nextTokens)
                    element.setAttribute(token, '');
                tokens = nextTokens;
                return;
            }
            if (isRef(resolved) || typeof resolved === 'function') {
                clearTokens();
                clearSpread();
                const nextTarget = isRef(resolved) ? resolved.target : resolved;
                if (refTarget !== nextTarget) {
                    clearRef();
                    refTarget = nextTarget;
                    applyRef(nextTarget, element);
                }
                return;
            }
            if (isSpread(resolved) || isPlainAttributeBag(resolved)) {
                clearTokens();
                clearRef();
                const props = isSpread(resolved) ? resolved.props : resolved;
                commitSpreadBag(element, props, spreadMemos);
                return;
            }
            // A template instance, DOM node, or any other branded descriptor has
            // no element-part markup form (spec: "not branded, not a Node, not a
            // template instance") — clear whatever this part previously applied
            // rather than spreading its internal shape onto the DOM as attributes.
            clearTokens();
            clearRef();
            clearSpread();
        },
        destroy() {
            clearRef();
            if (element) {
                for (const memo of spreadMemos.values()) {
                    if (memo.listener) {
                        element.removeEventListener(memo.name, memo.listener, memo.options);
                    }
                }
            }
            spreadMemos.clear();
        },
    };
}
// ---------------------------------------------------------------------------
// Node-family parts delegate reconciliation to render-dom-nodes.
// ---------------------------------------------------------------------------
/**
 * A node part's commit and teardown read nothing but the part itself, so both
 * are declared once here and every part points at the same two functions.
 * As object-literal methods they were a fresh closure per part per method —
 * four allocations per row of a keyed list whose row holds two node parts,
 * none of them distinguishable from these.
 */
function commitNodePart(value) {
    updateNodePart(this, value);
}
function destroyNodePart() {
    destroyMaterialized(this.materialized);
    for (const entry of this.entries?.values() ?? []) {
        destroyRepeatEntry(entry);
    }
    this.entries?.clear();
    this.entryOrder = undefined;
    for (const entry of this.cacheMap?.values() ?? []) {
        // The epoch, not the container. `cache()` holds its entries for the life of
        // the part, and both the entry's owner and the instance's container follow
        // placement — so they AGREE again the moment the instance is re-mounted
        // into the same element, and a container comparison would tear down a live
        // instance. Only the epoch distinguishes this mount from the one the entry
        // owns (#1335).
        unmountInstanceIfOwned(entry.instance, entry.owner, entry.epoch);
    }
    this.cacheMap?.clear();
    this.destroyed = true;
    removeRange(this);
}
/**
 * `entries` is deliberately absent, not an empty map: only a `repeat()` value
 * ever fills it, and the reconciler creates it on the first repeat commit.
 * Every text, template and conditional binding is a node part too, so a
 * 1,000-row list was allocating two permanently empty maps per row.
 *
 * `anchor`/`end` and `rangeParent` are the forms of the same bound range
 * (#1354, #1390) and every part is built with all three keys assigned, so
 * every form shares one hidden class. In the marker form the bounds are the
 * two boundary comments; in the sibling-bounded form (#1390) they are the
 * marker's static neighbour nodes.
 */
export function createNodePart(spec, anchor, end, rangeParent, documentObj, domAdapter) {
    return {
        spec,
        anchor,
        end,
        rangeParent,
        documentObj,
        domAdapter,
        nodes: [],
        materialized: null,
        textNode: null,
        update: commitNodePart,
        destroy: destroyNodePart,
    };
}
/**
 * Commits a static node or text binding straight into its elided range, with
 * no part object to retain (#1398). Only an elided hole (#1354) can be a
 * static slot — a non-elided one keeps a live part so its `<!--/h:N-->`
 * boundary matches the string renderer — so the range is the parent element's
 * children: one Text node created empty and then filled, the same two steps
 * the live path takes, updated in place when a later static value replaces it.
 */
export function commitStaticNodeText(documentObj, rangeParent, value) {
    const text = textValue(value);
    const existing = rangeParent.firstChild;
    if (existing && existing.nodeType === 3) {
        existing.data = text;
        return;
    }
    const node = documentObj.createTextNode('');
    rangeParent.insertBefore(node, null);
    node.data = text;
}
//# sourceMappingURL=render-dom-parts.js.map